'use client'

import { useEffect } from 'react'
import { Button } from '@ui/base/button'
import { typeClass } from '@utils/typography'
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react'

type Props = {
  /** Доля заполненного пути, от 0 до 100. */
  percent: number
  onNext: () => void
  /** Не передан — значит идти назад некуда: кнопка остаётся на месте, но выключена. */
  onBack?: () => void
  nextLabel?: string
  isNextDisabled?: boolean
}

/**
 * Enter листает шаги, но не всегда: в многострочном поле он переносит строку, на кнопке и ссылке
 * браузер сам их нажимает, в поле с подсказками выбирает найденное (город, склад, категорию),
 * в поле выбора файла открывает окно выбора, а во время набора иероглифов клавиша принадлежит
 * редактору ввода. Зажатая клавиша тоже не листает: повторы приходят быстрее перерисовки, и шаги
 * пролетали бы пачкой, а на последнем — уходило бы несколько заявок подряд.
 */
const shouldStepForward = (event: KeyboardEvent) => {
  if (event.key !== 'Enter' || event.repeat || event.isComposing) {
    return false
  }
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return false
  }

  const target = event.target
  if (!(target instanceof HTMLElement)) {
    return true
  }
  if (target instanceof HTMLInputElement && target.type === 'file') {
    return false
  }

  return (
    !['TEXTAREA', 'BUTTON', 'A'].includes(target.tagName) &&
    target.getAttribute('role') !== 'combobox' &&
    !target.isContentEditable
  )
}

/**
 * Подвал пошаговой формы: сколько пути пройдено и куда двигаться дальше. Общий для мастера заявки
 * на просчёт и формы карточки фулфилмента, поэтому живёт в общих элементах, а не рядом с ними.
 */
export const WizardFooter = ({ percent, onNext, onBack, nextLabel = 'Далее', isNextDisabled = false }: Props) => {
  useEffect(() => {
    if (isNextDisabled) {
      return
    }

    const step = (event: KeyboardEvent) => {
      if (shouldStepForward(event)) {
        event.preventDefault()
        onNext()
      }
    }

    document.addEventListener('keydown', step)

    return () => document.removeEventListener('keydown', step)
  }, [isNextDisabled, onNext])

  return (
    // Скругление снизу такое же, как у карточки: подвал стоит её последней строкой.
    <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-5 rounded-b-xl border-t bg-accent px-5 py-5 sm:px-7">
      <div className="grid min-w-56 flex-1 gap-2">
        <p className={typeClass.supporting}>
          <span className="text-muted-foreground">Готово: </span>
          <span className="font-medium tabular-nums">{percent}%</span>
        </p>
        <div
          role="progressbar"
          aria-label="Заполнение формы"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-2 w-full overflow-hidden rounded-full bg-border"
        >
          {/* Полоса заштрихована по диагонали: сплошная заливка на узкой полосе читается как фон. */}
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300 motion-reduce:transition-none"
            style={{
              width: `${percent}%`,
              backgroundImage: 'repeating-linear-gradient(115deg, rgba(255,255,255,0.28) 0 6px, transparent 6px 12px)',
            }}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Скругление и заливка приходят из дизайн-системы: своих значений подвал не задаёт. */}
        <Button
          type="button"
          variant="secondary"
          size="icon"
          aria-label="Назад"
          disabled={onBack === undefined}
          onClick={onBack}
        >
          <ArrowLeftIcon />
        </Button>
        <Button type="button" size="lg" disabled={isNextDisabled} onClick={onNext}>
          {nextLabel}
          <ArrowRightIcon />
        </Button>
      </div>
    </div>
  )
}
